import React, { useState } from 'react';
import styles from './StatsCard.module.css';

/**
 * Expandable Stats Card Component
 * 
 * @param {Object} props
 * @param {string} props.label - The card label (e.g. "Current Period")
 * @param {number|string} props.title - The main value displayed (e.g. "45")
 * @param {number} props.percentage - The percentage change value (e.g. 20)
 * @param {boolean} props.isRising - Whether the percentage is rising (green) or falling (red)
 * @param {Array} props.subFields - Array of objects with {label, value} for expanded view
 */
const StatsCard = ({ 
  label = "Current Period", 
  title = "0", 
  percentage = 0, 
  isRising = true,
  subFields = []
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const svgFill = isRising ? "var(--sentiment-background-positive)" : "var(--sentiment-background-positive)";
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };
  
  return (
    <div className={styles.card}>
      {/* Background wave pattern covering entire card */}
      <div className={styles.backgroundContainer}>
        <svg className={isRising ? styles.backgroundContainerRisingSvg : styles.backgroundContainerLoweringSvg} viewBox="0 0 166 101" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path opacity="0.6" d="M6.41636 59.1865L-2 101H154C160.627 101 166 95.6274 166 89V7.42973C166 0.0517159 156.27 -2.62025 152.502 3.72301C150.854 6.49722 147.573 7.85034 144.448 7.04393L137.494 5.24897C129.084 3.07831 120.478 8.01372 118.104 16.3687L113.803 31.5081C110.407 43.4618 95.2752 47.1501 86.7596 38.0999L84.1043 35.2779C79.7969 30.7001 72.9413 29.6092 67.4257 32.6238L66.3117 33.2327C61.6948 35.7562 55.9755 34.9991 52.1741 31.3615C46.6585 26.0834 37.6901 27.1854 33.6163 33.6419L30.4127 38.7194C28.5243 41.7122 25.6971 43.9946 22.3733 45.2093L16.6095 47.3159C11.3768 49.2283 7.51569 53.7249 6.41636 59.1865Z" 
          />
        </svg>
      </div>
      
      {/* Card header - always visible */}
      <div 
        className={styles.header}
        onClick={toggleExpanded}
      >
        {/* Card content */}
        <div className={styles.content}>
          {/* Label */}
          <div className={styles.labelContainer}>
            <div className={styles.label}>
              {label}
            </div>
            <div className={`${styles.arrow} ${isExpanded ? styles.arrowExpanded : ''}`}>
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="8" viewBox="0 0 15 8" fill="none">
                <path d="M14.0563 1.58525L7.85046 7.79723C7.77658 7.87097 7.69655 7.92307 7.61036 7.95355C7.52417 7.98452 7.43182 8 7.33331 8C7.23481 8 7.14246 7.98452 7.05627 7.95355C6.97008 7.92307 6.89004 7.87097 6.81616 7.79724L0.591888 1.58525C0.419504 1.41321 0.333313 1.19816 0.333313 0.940093C0.333313 0.682028 0.425661 0.46083 0.610357 0.276498C0.795054 0.0921664 1.01053 9.24072e-07 1.2568 9.13308e-07C1.50306 9.02543e-07 1.71854 0.0921664 1.90323 0.276498L7.33331 5.69585L12.7634 0.276498C12.9358 0.104455 13.1481 0.018433 13.4002 0.018433C13.6529 0.018433 13.8716 0.110599 14.0563 0.294931C14.241 0.479263 14.3333 0.694316 14.3333 0.940092C14.3333 1.18587 14.241 1.40092 14.0563 1.58525Z" fill="var(--static-text-strong)"/>
              </svg>
            </div>
          </div>
          
          {/* Main value */}
          <div className={styles.titleContainer}>
            <div className={styles.title}>
              {title}
            </div>
          </div>
          
          {/* Percentage indicator */}
          <div className={styles.percentageContainer}>
            <div className={`${styles.percentageBadge} ${isRising ? styles.rising : styles.falling}`}>
              {isRising ? 
                <svg className={styles.percentageIcon} xmlns="http://www.w3.org/2000/svg" width="15" height="14" viewBox="0 0 15 14" fill="none">
                  <mask id="mask0_4071_3651" maskUnits="userSpaceOnUse" x="0" y="0" width="15" height="14">
                  <rect x="0.333252" width="14" height="14" fill="#D9D9D9"/>
                  </mask>
                  <g mask="url(#mask0_4071_3651)">
                  <path d="M2.31667 10.5L1.5 9.68333L5.81667 5.3375L8.15 7.67083L11.1833 4.66667H9.66667V3.5H13.1667V7H12V5.48333L8.15 9.33333L5.81667 7L2.31667 10.5Z" fill="#006718"/>
                </g>
              </svg> 
              : <svg className={styles.percentageIconFalling} xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M9.33329 10.5V9.33333H10.85L7.81663 6.32917L5.48329 8.6625L1.16663 4.31667L1.98329 3.5L5.48329 7L7.81663 4.66667L11.6666 8.51667V7H12.8333V10.5H9.33329Z"/>
              </svg>}
              <span className={styles.percentageText}>
                {percentage}%
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Expandable section */}
      {isExpanded && subFields.length > 0 && (
        <div className={styles.expandedSection}>
          <div className={styles.subFieldsGrid}>
            {subFields.map((field, index) => (
              <div 
                key={index} 
                className={`${styles.subFieldItem} ${
                  index === subFields.length - 1 && subFields.length % 2 !== 0 
                    ? styles.subFieldFullWidth 
                    : ''
                }`}
              >
                <div className={styles.subFieldLabel}>
                  {field.label}
                </div>
                <div className={styles.subFieldValue}>
                  {field.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsCard;